import { useEffect, useState } from "react";
import { Loader2, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVault } from "@/context/VaultContext";
import { CameraCapture } from "@/components/aph/CameraCapture";

const DURATIONS = [3, 5, 10];

export function SnapDialog({ open, onOpenChange, sid, reply = false }) {
  const { sendMessage } = useVault();
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [duration, setDuration] = useState(5);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!blob) return setUrl(null);
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const close = (o) => { if (!o) setBlob(null); onOpenChange(o); };

  const send = async () => {
    setBusy(true);
    try {
      await sendMessage(sid, { snap: { blob, duration, reply } });
      close(false);
    } catch (e) {
      toast.error(e.message || "Snap failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="snap-dialog" className="bg-nebula border-rim text-ash sm:max-w-sm rounded-secure">
        <DialogHeader>
          <DialogTitle className="font-branding">{reply ? "Reply with a Snap" : "View-once Snap"}</DialogTitle>
          <DialogDescription className="text-ash/50">Opens once, for a few seconds, then burns on both devices.</DialogDescription>
        </DialogHeader>
        {open && !blob && <CameraCapture onCapture={setBlob} />}
        {url && (
          <div className="space-y-4">
            <img src={url} alt="Snap preview" className="w-full aspect-[3/4] object-cover rounded-secure border border-glass" data-testid="snap-preview" />
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.16em] text-ash/45 font-branding">View time</span>
              <div className="flex gap-1.5">
                {DURATIONS.map((d) => (
                  <button key={d} onClick={() => setDuration(d)} data-testid={`snap-duration-${d}`} data-state={d === duration ? "on" : "off"}
                    className={`w-11 py-1.5 rounded-secure border text-sm font-mono transition-colors ${d === duration ? "border-flare text-flare shadow-flare-glow" : "border-rim text-ash/55"}`}>{d}s</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setBlob(null)} data-testid="snap-retake-btn" className="flex-1 flex items-center justify-center gap-2 border border-rim rounded-secure py-2.5 text-sm hover:text-stellar"><RotateCcw size={16} strokeWidth={1.5} /> Retake</button>
              <button onClick={send} disabled={busy} data-testid="snap-send-btn" className="flex-1 flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-2.5 disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={1.5} />} Send Snap
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
